/**
 * Play scripts — saved play calls, tags, and reps built from them.
 *
 * A coach saves each call once ("88 Super Power") and the tags he hangs on
 * calls ("Tight", "on 2"). A script is an ordinary drill whose steps are reps;
 * a rep is a row of parts — the call, its tags, and any one-off words — in the
 * order he says them. Renaming a call renames it in every script, because a
 * rep points at the call rather than copying its words.
 *
 * Nothing here assumes a calling system. Tags have no fixed order: each one
 * remembers where it last sat relative to the call (`TagData.pos`) and lands
 * there next time, so every coach's own habit is what gets learned.
 *
 * Compatibility rules (see DrillData.reps / Assignment.script):
 *  - `steps` is always written as the resolved wording, so the shared library,
 *    printing and older readers never need to know about reps;
 *  - a drill with no call in any step saves with no `reps` at all — plain
 *    drills are byte-for-byte what they were;
 *  - a practice that has been run keeps the wording it ran (freezePeriods).
 */
import type {
  Assignment,
  CallData,
  DrillData,
  FrozenRep,
  LibraryItemRow,
  Period,
  RepPart,
  ScriptRep,
  TagData,
} from '../types';

export interface ScriptLib {
  /** Includes trashed calls (gone: true) so an old rep can still show its name. */
  calls: Map<string, CallData & { gone?: boolean }>;
  tags: Map<string, TagData & { gone?: boolean }>;
}

export const EMPTY_LIB: ScriptLib = { calls: new Map(), tags: new Map() };

/** Build the lookup from library rows — pass live AND trashed rows. */
export function scriptLib(rows: readonly LibraryItemRow<unknown>[]): ScriptLib {
  const lib: ScriptLib = { calls: new Map(), tags: new Map() };
  // Live rows win over a trashed row with the same id (there shouldn't be one).
  const sorted = [...rows].sort((a, b) => Number(!a.deleted_at) - Number(!b.deleted_at));
  for (const r of sorted) {
    if (r.kind === 'call') lib.calls.set(r.id, { ...(r.data as CallData), id: r.id, gone: !!r.deleted_at });
    else if (r.kind === 'tag') lib.tags.set(r.id, { ...(r.data as TagData), id: r.id, gone: !!r.deleted_at });
  }
  return lib;
}

// ─── parts & reps ───────────────────────────────────────────────────────────
export const isCallPart = (p: RepPart): p is { call: string; w: string } => 'call' in p;
export const isTagPart = (p: RepPart): p is { tag: string; w: string } => 'tag' in p;
export const isTextPart = (p: RepPart): p is { text: string } => 'text' in p;

export function partText(p: RepPart, lib: ScriptLib): string {
  if (isCallPart(p)) return lib.calls.get(p.call)?.name || p.w;
  if (isTagPart(p)) return lib.tags.get(p.tag)?.name || p.w;
  return p.text;
}

const squash = (s: string) => s.replace(/\s+/g, ' ').trim();

export function repText(rep: ScriptRep, lib: ScriptLib): string {
  return squash(rep.parts.map((p) => partText(p, lib)).join(' '));
}

/** A rep with no call and no tag is a plain typed step. */
export function isTypedRep(rep: ScriptRep): boolean {
  return !rep.parts.some((p) => isCallPart(p) || isTagPart(p));
}

export function typedRep(text: string): ScriptRep {
  return { parts: [{ text }] };
}

export function repCallId(rep: ScriptRep): string | null {
  const p = rep.parts.find(isCallPart);
  return p ? p.call : null;
}

export function hasChips(reps: readonly ScriptRep[] | undefined): boolean {
  return !!reps && reps.some((r) => !isTypedRep(r));
}

/** Whether a drill edits in the play-script builder: made as a script, or
 *  already holding reps. */
export function isScriptDrill(d: Pick<DrillData, 'script' | 'reps'>): boolean {
  return d.script === true || hasChips(d.reps);
}

/** The reps a drill edits as: its saved reps, or its typed steps. */
export function drillReps(d: Pick<DrillData, 'reps' | 'steps'>): ScriptRep[] {
  if (d.reps && d.reps.length) return JSON.parse(JSON.stringify(d.reps)) as ScriptRep[];
  return (d.steps ?? []).map(typedRep);
}

export function stepsFromReps(reps: readonly ScriptRep[], lib: ScriptLib): string[] {
  return reps.map((r) => repText(r, lib)).filter(Boolean);
}

/**
 * What a drill saves as. Empty reps are dropped, call/tag parts get their
 * current wording stamped into `w`, `steps` is rewritten from the reps, and a
 * drill with no call anywhere loses `reps` entirely.
 */
export function drillWithReps(d: DrillData, reps: readonly ScriptRep[], lib: ScriptLib): DrillData {
  const kept = reps
    .map((r) => ({ parts: r.parts.filter((p) => !isTextPart(p) || p.text.trim()) }))
    .filter((r) => r.parts.length > 0)
    .map((r) => ({
      parts: r.parts.map((p): RepPart =>
        isCallPart(p) ? { call: p.call, w: partText(p, lib) } : isTagPart(p) ? { tag: p.tag, w: partText(p, lib) } : { text: squash(p.text) },
      ),
    }));
  const out: DrillData = { ...d, steps: stepsFromReps(kept, lib) };
  if (hasChips(kept)) out.reps = kept;
  else delete out.reps;
  return out;
}

/**
 * The steps to show for a drill, as {wording, linked play}. A script resolves
 * its reps against the current calls; any other drill shows its typed steps.
 */
export function scriptLines(d: Pick<DrillData, 'reps' | 'steps'>, lib: ScriptLib): FrozenRep[] {
  if (hasChips(d.reps)) {
    return d.reps!.map((r) => {
      const cid = repCallId(r);
      const call = cid ? lib.calls.get(cid) : undefined;
      const line: FrozenRep = { t: repText(r, lib) };
      if (call?.playId) line.play = call.playId;
      return line;
    }).filter((l) => l.t);
  }
  return (d.steps ?? []).filter((s) => s.trim()).map((t) => ({ t }));
}

/** When a drill's saved steps no longer match its reps (a call was renamed),
 *  the drill with fresh steps — otherwise null. */
export function refreshSteps(d: DrillData, lib: ScriptLib): DrillData | null {
  if (!hasChips(d.reps)) return null;
  const steps = stepsFromReps(d.reps!, lib);
  const parts = d.reps!.map((r) => ({ parts: r.parts.map((p): RepPart => (isCallPart(p) ? { call: p.call, w: partText(p, lib) } : isTagPart(p) ? { tag: p.tag, w: partText(p, lib) } : p)) }));
  if (JSON.stringify(steps) === JSON.stringify(d.steps ?? []) && JSON.stringify(parts) === JSON.stringify(d.reps)) return null;
  return { ...d, steps, reps: parts };
}

/** How many reps across these drills use a call or a tag. */
export function partUses(drills: readonly Pick<DrillData, 'reps'>[], id: string): number {
  let n = 0;
  for (const d of drills) for (const r of d.reps ?? []) if (r.parts.some((p) => (isCallPart(p) && p.call === id) || (isTagPart(p) && p.tag === id))) n++;
  return n;
}

// ─── typing a rep ───────────────────────────────────────────────────────────
interface VocabEntry {
  id: string;
  kind: 'call' | 'tag';
  name: string;
  toks: string[];
}

const tokens = (s: string) => s.toLowerCase().split(/\s+/).filter(Boolean);

function vocabOf(lib: ScriptLib): VocabEntry[] {
  const out: VocabEntry[] = [];
  for (const c of lib.calls.values()) if (!c.gone && c.name.trim()) out.push({ id: c.id, kind: 'call', name: c.name, toks: tokens(c.name) });
  for (const t of lib.tags.values()) if (!t.gone && t.name.trim()) out.push({ id: t.id, kind: 'tag', name: t.name, toks: tokens(t.name) });
  // Longest phrase first, so "Waggle 88 Pass" beats "88", calls before tags on a tie.
  return out.sort((a, b) => b.toks.length - a.toks.length || (a.kind === b.kind ? 0 : a.kind === 'call' ? -1 : 1));
}

/** The one live call whose name starts with this word ("88" → "88 Super Power"). */
function shorthandCall(word: string, lib: ScriptLib): VocabEntry | null {
  const w = word.toLowerCase();
  const hits = [...lib.calls.values()].filter((c) => !c.gone && tokens(c.name)[0] === w && tokens(c.name).length > 1);
  return hits.length === 1 ? { id: hits[0]!.id, kind: 'call', name: hits[0]!.name, toks: [w] } : null;
}

/**
 * Turn a typed line into a rep. Saved calls and tags are matched by name
 * (any case); a word that starts exactly one call's name stands for that call
 * when it ends the line or a saved tag follows ("88 on 2", not "88 Dive");
 * anything else stays as the words typed. A line with no call in it is a plain
 * step — so ordinary drill steps typed here stay ordinary text.
 */
export function parseRep(text: string, lib: ScriptLib): ScriptRep {
  const raw = text.trim().split(/\s+/).filter(Boolean);
  const vocab = vocabOf(lib);
  const parts: RepPart[] = [];
  let haveCall = false;
  let i = 0;
  const exactAt = (at: number, callsToo: boolean): VocabEntry | null => {
    for (const v of vocab) {
      if (v.kind === 'call' && !callsToo) continue;
      if (v.toks.every((t, k) => (raw[at + k] ?? '').toLowerCase() === t)) return v;
    }
    return null;
  };
  const matchAt = (at: number): VocabEntry | null => {
    const exact = exactAt(at, !haveCall);
    if (exact) return exact;
    // Shorthand ("88" for "88 Super Power") only when nothing unknown follows
    // it: the line ends there or goes on with a saved tag or call ("88 on 2").
    // "26 Dive" is a different play than "26 Power" — keep it as typed.
    if (!haveCall && raw[at] && (at + 1 >= raw.length || exactAt(at + 1, true))) return shorthandCall(raw[at]!, lib);
    return null;
  };
  while (i < raw.length) {
    const hit = matchAt(i);
    if (hit) {
      parts.push(hit.kind === 'call' ? { call: hit.id, w: hit.name } : { tag: hit.id, w: hit.name });
      if (hit.kind === 'call') haveCall = true;
      i += hit.toks.length;
      continue;
    }
    const last = parts[parts.length - 1];
    if (last && isTextPart(last)) last.text += ' ' + raw[i];
    else parts.push({ text: raw[i]! });
    i++;
  }
  if (!haveCall) return typedRep(squash(text));
  return { parts };
}

export interface Suggestion {
  id: string;
  kind: 'call' | 'tag';
  name: string;
  /** How many trailing words the suggestion replaces. */
  replaces: number;
}

/** Calls and tags that finish what is being typed (the last one to three words). */
export function suggest(text: string, lib: ScriptLib, max = 6): Suggestion[] {
  if (!text.trim() || /\s$/.test(text)) return [];
  const words = text.trim().split(/\s+/);
  const out: Suggestion[] = [];
  const seen = new Set<string>();
  for (let k = Math.min(3, words.length); k >= 1; k--) {
    const frag = words.slice(-k).join(' ').toLowerCase();
    for (const v of vocabOf(lib)) {
      const n = v.name.toLowerCase();
      if (seen.has(v.id) || n === frag || !n.startsWith(frag)) continue;
      seen.add(v.id);
      out.push({ id: v.id, kind: v.kind, name: v.name, replaces: k });
    }
  }
  return out
    .sort((a, b) => b.replaces - a.replaces || (a.kind === b.kind ? a.name.localeCompare(b.name) : a.kind === 'call' ? -1 : 1))
    .slice(0, max);
}

export function applySuggestion(text: string, s: Suggestion): string {
  const words = text.trim().split(/\s+/);
  return [...words.slice(0, words.length - s.replaces), s.name].join(' ') + ' ';
}

// ─── tag order ──────────────────────────────────────────────────────────────
/** Default spot for a tag that has never been placed: in front of the call. */
export const DEFAULT_TAG_POS = -1;

/**
 * Add a tag where it usually goes. Its remembered `pos` is its offset from
 * the call (−1 = just before, 1 = just after); it goes in at that distance,
 * without jumping over a neighbour that sits farther out.
 */
export function placeTag(parts: readonly RepPart[], tagId: string, lib: ScriptLib): RepPart[] {
  const tag = lib.tags.get(tagId);
  const part: RepPart = { tag: tagId, w: tag?.name ?? '' };
  const out = [...parts];
  const ci = out.findIndex(isCallPart);
  if (ci < 0) return [...out, part];
  const pos = tag?.pos || DEFAULT_TAG_POS;
  // A neighbour's distance from the call: where its tag usually goes, or
  // where it is now for a one-off word.
  const dist = (i: number) => {
    const p = out[i]!;
    const remembered = isTagPart(p) ? lib.tags.get(p.tag)?.pos : undefined;
    return Math.abs(remembered || i - ci);
  };
  if (pos < 0) {
    // Before the call: step outward past neighbours that sit closer to the call.
    let at = ci;
    while (at > 0 && dist(at - 1) < -pos) at--;
    out.splice(at, 0, part);
  } else {
    let at = ci + 1;
    while (at < out.length && dist(at) <= pos) at++;
    out.splice(at, 0, part);
  }
  return out;
}

/** Where each tag in this rep sits relative to its call — what to remember. */
export function tagPositions(parts: readonly RepPart[]): Map<string, number> {
  const m = new Map<string, number>();
  const ci = parts.findIndex(isCallPart);
  if (ci < 0) return m;
  parts.forEach((p, i) => {
    if (isTagPart(p)) m.set(p.tag, i - ci);
  });
  return m;
}

/** Put the call into a rep that has none: after the tags that go before calls. */
export function placeCall(parts: readonly RepPart[], callId: string, lib: ScriptLib): RepPart[] {
  const call = lib.calls.get(callId);
  const part: RepPart = { call: callId, w: call?.name ?? '' };
  const ci = parts.findIndex(isCallPart);
  if (ci >= 0) return parts.map((p, i) => (i === ci ? part : p));
  let at = 0;
  while (at < parts.length) {
    const p = parts[at]!;
    const before = isTagPart(p) ? (lib.tags.get(p.tag)?.pos || DEFAULT_TAG_POS) < 0 : isTextPart(p);
    if (!before) break;
    at++;
  }
  const out = [...parts];
  out.splice(at, 0, part);
  return out;
}

export function movePart(parts: readonly RepPart[], from: number, to: number): RepPart[] {
  const out = [...parts];
  const [p] = out.splice(from, 1);
  out.splice(Math.max(0, Math.min(out.length, to)), 0, p!);
  return out;
}

/** Steps that would become call chips if converted, out of all typed steps. */
export function convertible(d: Pick<DrillData, 'reps' | 'steps'>, lib: ScriptLib): number {
  if (hasChips(d.reps)) return 0;
  return (d.steps ?? []).filter((s) => !isTypedRep(parseRep(s, lib))).length;
}

// ─── plans ──────────────────────────────────────────────────────────────────
type LegacyPeriod = Period & { drillId?: string; customActivity?: string; notes?: string; groups?: string[] };

/** A period's assignments; plugin-era periods held one drill inline. */
export function normalizeAssignments(p: LegacyPeriod): Assignment[] {
  if (Array.isArray(p.assignments)) return p.assignments;
  if (p.drillId || p.customActivity || p.notes || p.groups?.length) {
    return [{ drillId: p.drillId || '', customActivity: p.customActivity || '', groups: p.groups || [], notes: p.notes || '' }];
  }
  return [];
}

/**
 * What a period's sheet shows for an assignment: the script as it was run if
 * this plan has been run with this drill, else the drill's current script,
 * else its typed steps.
 */
export function assignmentLines(a: Assignment, drill: Pick<DrillData, 'reps' | 'steps'> | undefined, lib: ScriptLib): { lines: FrozenRep[]; asRun: boolean } {
  if (a.script && a.script.drill === a.drillId) return { lines: a.script.reps, asRun: true };
  return { lines: drill ? scriptLines(drill, lib) : [], asRun: false };
}

/**
 * Freeze every script in a plan at the wording it is about to be run with.
 * Called when the practice timer starts, so a call renamed next month doesn't
 * rewrite what this practice ran. Returns the same array (changed: false) when
 * nothing differs, so a re-start doesn't trigger a save.
 */
export function freezePeriods(
  periods: readonly Period[],
  drillById: (id: string) => Pick<DrillData, 'reps' | 'steps'> | undefined,
  lib: ScriptLib,
): { periods: Period[]; changed: boolean } {
  let changed = false;
  const next = periods.map((p) => {
    const as = normalizeAssignments(p as LegacyPeriod);
    let pChanged = false;
    const nas = as.map((a) => {
      const d = a.drillId ? drillById(a.drillId) : undefined;
      const script = d && hasChips(d.reps) ? { drill: a.drillId, reps: scriptLines(d, lib) } : undefined;
      if (JSON.stringify(script) === JSON.stringify(a.script)) return a;
      // A drill that isn't a script any more keeps an old snapshot only if it was for a different drill.
      if (!script && (!a.script || a.script.drill !== a.drillId)) return a;
      pChanged = true;
      const na: Assignment = { ...a };
      if (script) na.script = script;
      else delete na.script;
      return na;
    });
    if (!pChanged) return p;
    changed = true;
    const np = { ...(p as LegacyPeriod), assignments: nas } as LegacyPeriod;
    delete np.drillId;
    delete np.customActivity;
    delete np.notes;
    delete np.groups;
    return np as Period;
  });
  return { periods: changed ? next : (periods as Period[]), changed };
}
